/*
 * Copyright (C) iSchoolConnect - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import axios from 'axios';
import { Subject } from 'rxjs';
import { StorageService } from './StorageService';
import {
	BotMessageRequestBody,
	feedbackDetailsBody,
	RawBotResponse,
	RawMessageContent,
} from '../types';

const GPT_URL = process.env.REACT_APP_GPT_URL as string;
if (!GPT_URL) {
	throw new Error('Environment variables are missing.');
}

/**
 * Retrieves the custom GPT URL if configured, otherwise returns the default GPT URL.
 * @param currentGPTURL The default GPT URL to use if no custom URL is configured.
 * @returns The custom GPT URL if available, otherwise the provided default GPT URL.
 */

const checkForCustomGPTUrl = (currentGPTURL: string) =>
	StorageService?.config?.org_config?.custom_gpt_url || currentGPTURL;

/**
 * Creates a room on the GPT server.
 * @returns {Promise<{ roomId: string; messages: RawMessageContent[]; message: string }>}
 */
const createRoomGPT = async (): Promise<{
	roomId: string;
	messages?: RawMessageContent[];
	message?: string;
}> => {
	const response = await axios({
		method: 'post',
		url: `${checkForCustomGPTUrl(GPT_URL)}/create_a_room`,
		headers: { 'x-tenant-identifier': StorageService.config.identifier || 'unknown' },
	});

	return {
		roomId: response?.data?.room_id,
		messages: response?.data?.data?.messages || response?.data?.messages || [],
		message: response?.data?.message || '',
	};
};

/**
 * Sends a message to the GPT server.
 * @param message The message to send to the GPT server.
 * @param roomId The room id of the room to send the message to.
 * @param response$ The response subject to send the streaming response to.
 * @returns The "promised" response from the GPT server.
 */
const sendMessage = async (message: string, roomId: string, response$: Subject<string>) => {
	const response = await fetch(`${checkForCustomGPTUrl(GPT_URL)}/chatbotresponse`, {
		method: 'POST',
		headers: { 'x-tenant-identifier': StorageService.config.identifier || 'unknown' },
		body: JSON.stringify({
			input: message,
			room_id: roomId,
		}),
	});
	if (!response.ok || !response.body) {
		throw response.statusText;
	}

	let fullResponse = '';
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	const loopRunner = true;

	while (loopRunner) {
		// Here we start reading the stream, until its done.
		// eslint-disable-next-line no-await-in-loop
		const { value, done } = await reader.read();
		if (done) {
			break;
		}
		const decodedChunk = decoder.decode(value, { stream: true });
		fullResponse += decodedChunk;
		response$.next(fullResponse);
	}

	return fullResponse;
};

/**
 * Sends a message to the ChatBotGPT_V2 server.
 * @param body The message object to send to the GPT_V2 server.
 * @returns The "promised" response from the GPT_V2 server.
 */
const sendMessageChatGPT_V2 = async (body: BotMessageRequestBody): Promise<RawBotResponse> => {
	const response = await axios({
		method: 'post',
		headers: { 'x-tenant-identifier': StorageService.config.identifier || 'unknown' },
		url: `${checkForCustomGPTUrl(GPT_URL)}/chatbotresponse_JSON`,
		data: {
			room_id: body.room_id,
			input: body.text,
		},
	});

	return response.data as RawBotResponse;
};

/**
 * Sends feedback details to the server.
 * @param body The feedback object to send to the server.
 * @returns The "promised" response from the server.
 */
const sendFeedbackDetails = async (body: feedbackDetailsBody) => {
	try {
		await axios({
			method: 'post',
			headers: { 'x-tenant-identifier': StorageService.config.identifier || 'unknown' },
			url: `${checkForCustomGPTUrl(GPT_URL)}/give_feedback`,
			data: {
				room_id: body.room_id,
				message_id: body.message_id,
				feedback_details: body.feedback_details,
			},
		});

		StorageService.storeMessageIdOfFeedbackSubmitted(body.message_id);
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error('Error sending feedback details:', error);
	}
};

/**
 * The GPT service, handles all the GPT related requests.
 */
export const GPTService = {
	createRoomGPT,
	sendMessage,
	sendMessageChatGPT_V2,
	sendFeedbackDetails,
};
